import styled from '@emotion/styled';
import React, { FC, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Hackathon } from '../../Api';
import { useApiClient } from '../../hooks/useApiClient';
import AppToolbar from '../shared/AppToolbar';
import { Page, PageBody, PageTitle } from '../shared/Layout';
import { ButtonLink } from '../shared/Button';
import Require from '../shared/Require';
import theme from '../shared/theme';

const HackathonListHome: FC = () => {
  const apiClient = useApiClient();
  const [hackathons, setHackathons] = useState<Hackathon[] | null>(null);
  const [currentHackathon, setCurrentHackathon] = useState<Hackathon | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      // Fetch current hackathon first for immediate display
      // This is independent and should not block the list loading
      apiClient.hackathon.getCurrentSummary()
        .then((current) => {
          setCurrentHackathon(current);
        })
        .catch((error) => {
          console.error('Failed to load current hackathon:', error);
          // Don't fail the entire component, just log the error
        });

      // Fetch all hackathons in the background
      // This is also independent from the current hackathon fetch
      try {
        const allHackathons = await apiClient.hackathon.getAllSummary();
        
        // Sort hackathons by demoTime in descending order
        const sortedHackathons = allHackathons.sort((a, b) => 
          new Date(b.demoTime).getTime() - new Date(a.demoTime).getTime()
        );
        
        setHackathons(sortedHackathons);
      } catch (error) {
        console.error('Failed to load hackathons list:', error);
        // Set empty array to allow component to render with "no hackathons" state
        setHackathons([]);
      }
    };

    fetchData();
  }, [apiClient]);

  const getStageColor = (stage: string): string => {
    switch (stage) {
      case 'Planning':
        return theme.colors.lightGrey;
      case 'Hacking':
        return theme.colors.blue;
      case 'Demo':
        return theme.colors.orange;
      case 'Vote':
        return theme.colors.darkGreen;
      case 'Finished':
        return theme.colors.red;
      default:
        return theme.colors.lightGrey;
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin - Hackathon Selection</title>
      </Helmet>
      <Page>
        <AppToolbar hackathonId={undefined} />
        <PageBody>
          <PageTitle>Hackathon Administration</PageTitle>
          <Description>Select a hackathon to administer:</Description>
          <CreateButtonContainer>
            <CreateButton category="success" to="/hackathon/new">
              + Create New Hackathon
            </CreateButton>
          </CreateButtonContainer>
          <Require required={hackathons}>
            {() => (
              <HackathonList>
                {hackathons!.map((hackathon) => (
                  <HackathonListItem
                    key={hackathon.hackathonId}
                    to={`/hackathon/${hackathon.hackathonId}/admin`}
                    isCurrent={currentHackathon?.hackathonId === hackathon.hackathonId}
                  >
                    <HackathonInfo>
                      <HackathonName>
                        {hackathon.name}
                        {currentHackathon?.hackathonId === hackathon.hackathonId && (
                          <CurrentLabel>(Current)</CurrentLabel>
                        )}
                      </HackathonName>
                      <HackathonDate>
                        {new Date(hackathon.demoTime).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </HackathonDate>
                    </HackathonInfo>
                    <StageBadge color={getStageColor(hackathon.currentStage)}>
                      {hackathon.currentStage}
                    </StageBadge>
                  </HackathonListItem>
                ))}
              </HackathonList>
            )}
          </Require>
        </PageBody>
      </Page>
    </>
  );
};

const Description = styled.p`
  text-align: center;
  color: ${theme.colors.lightGrey};
  margin-bottom: 20px;
`;

const CreateButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
`;

const CreateButton = styled(ButtonLink)`
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
`;

const HackathonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
`;

const HackathonListItem = styled(Link)<{ isCurrent: boolean }>`
  ${theme.styles.materialPanel}
  background: #fff;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s, box-shadow 0.2s;
  border: ${(props) => (props.isCurrent ? `3px solid ${theme.colors.green}` : 'none')};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.16), 0 0 4px 0 rgba(0, 0, 0, 0.12);
  }
`;

const HackathonInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
`;

const HackathonName = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CurrentLabel = styled.span`
  color: ${theme.colors.green};
  font-size: 14px;
  font-weight: 500;
`;

const HackathonDate = styled.div`
  color: ${theme.colors.lightGrey};
  font-size: 14px;
`;

const StageBadge = styled.div<{ color: string }>`
  background-color: ${(props) => props.color};
  color: white;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export default HackathonListHome;
